// layouts — the SINGLE source of truth for grid geometry (#86bb5dx62).
//
// Raising the ceiling — adding a 25/36/64 live layout, or a 32-tile Arrange
// density (some Rosas systems have 32-ch single views) — is a ONE-LINE data edit
// HERE, not a scavenger hunt through switch statements. Consumed by the Live
// Layout sheet (LiveView), the live grid geometry (VideoViewWs), and the tablet
// Arrange density + canvas (VideoViewWs / LiveView).

// ── LIVE layouts ────────────────────────────────────────────────────────────
// The confs the Layout sheet offers + how VideoViewWs lays each one out.
//   tiles = cameras shown per page
//   cols/rows = the grid
//   span = the first tile's {cols,rows} footprint for an ASYMMETRIC layout
//          (conf-8's big top-left 3×3, mirroring Java VideoView.java:745). tileSpan() drops it in
//          PORTRAIT — a hero needs a wide screen, so mobile+tablet portrait render uniform instead.
//   colsPortrait = column override in a PORTRAIT orientation. Three distinct uses, and the
//          CALLER (VideoViewWs) decides the portrait SCOPE per conf:
//            - conf-2 STACKS (2 cols -> 1) in ANY portrait tier (mobile + tablet portrait);
//              it stays side-by-side (base cols:2) in every landscape tier AND desktop. Two
//              cameras side-by-side is the natural wide-screen layout (matches Java + the
//              conf-2 selector icon); stacking is only for a tall/narrow portrait viewport.
//            - conf-6/12 TRANSPOSE to fewer cols / more rows on a MOBILE-portrait phone
//              (conf-6 3x2->2x3, conf-12 4x3->3x4), letterboxing 16:9 tiles less (#86bb7phhh A2).
//            - conf-8 DE-HEROES in portrait: colsPortrait:2 + tileSpan()==null gives a uniform 2×4
//              (mobile + tablet portrait, <=1023) instead of the 1-big-7-tiny hero, which crushed the
//              7 PiPs to ~98px on a phone; landscape + desktop keep the base 4×4 + hero (#86bb7phhh).
//          Rows follow from ceil(cams / cols), so no rowsPortrait is needed. Base cols/rows are
//          UNCHANGED for the other confs, so their standard geometry holds everywhere.
export const LAYOUTS = [
  { conf: 'conf-1',  tiles: 1,  cols: 1, rows: 1 },
  { conf: 'conf-2',  tiles: 2,  cols: 2, rows: 1, colsPortrait: 1 },
  { conf: 'conf-4',  tiles: 4,  cols: 2, rows: 2 },
  { conf: 'conf-6',  tiles: 6,  cols: 3, rows: 2, colsPortrait: 2 },
  { conf: 'conf-8',  tiles: 8,  cols: 4, rows: 4, span: { cols: 3, rows: 3 }, colsPortrait: 2 },
  { conf: 'conf-9',  tiles: 9,  cols: 3, rows: 3 },
  { conf: 'conf-12', tiles: 12, cols: 4, rows: 3, colsPortrait: 3 },
  { conf: 'conf-16', tiles: 16, cols: 4, rows: 4 },
  // Bump the LIVE ceiling here (one line each):
  // { conf: 'conf-25', tiles: 25, cols: 5, rows: 5 },
  // { conf: 'conf-36', tiles: 36, cols: 6, rows: 6 },
];

// The Layout sheet's option order — driven off the config so a new conf shows up
// automatically (its SVG glyph is keyed by conf in LiveView).
export const LAYOUT_CONFS = LAYOUTS.map((l) => l.conf);

const byConf = LAYOUTS.reduce((m, l) => { m[l.conf] = l; return m; }, {});

export const getLayout = (conf) => byConf[conf] || byConf['conf-1'];

// Column count for a conf, honoring the portrait overrides (conf-2 stacks 2->1; conf-6/12
// transpose 3->2 / 4->3). VideoViewWs keeps its own rows math (ceil(n/cols)) so partially-filled
// live grids stay tight; this just centralizes cols + the conf-8 span. The CALLER decides the
// portrait SCOPE per conf (see VideoViewWs): both conf-2 and conf-6/12 use isMobilePortrait (which
// covers mobile AND tablet portrait, <=1023). Landscape + desktop always get the base cols.
export const gridColumns = (conf, { portrait = false } = {}) => {
  const l = getLayout(conf);
  if (portrait && l.colsPortrait) return l.colsPortrait;
  return l.cols;
};

// The conf-8 hero (big top-left 3×3, Java VideoView.java:745) is a WIDE-SCREEN layout. In portrait
// (mobile AND tablet portrait, <=1023 — the isMobilePortrait scope the CALLER passes, same as cols)
// there's no horizontal room for a hero + PiP strip: it collapses the other 7 tiles to ~98px. So
// portrait drops the span and renders a uniform grid instead (conf-8 gains colsPortrait:2 -> a clean
// 2×4, matching conf-4/6/9). Landscape + desktop keep the hero. #86bb7phhh mobile-first.
export const tileSpan = (conf, { portrait = false } = {}) => {
  if (portrait) return null;
  return getLayout(conf).span || null;
};

// confToNumber — the numeric tile count from a 'conf-N' key. Live stores the SHARED selected grid as
// the 'conf-N' string in state.config.conf; playback renders from a NUMBER (#86bb7phhh: Live + playback
// share one conf, either can change it). 'conf-fs' never reaches here (ConfButtonsReducer never persists
// fullscreen), and anything unparseable falls back to 4 (the desktop/tablet default grid).
export const confToNumber = (conf) => {
  const n = parseInt(String(conf).replace('conf-', ''), 10);
  return Number.isFinite(n) ? n : 4;
};

// tilesForCount — the smallest layout tile-count (1/2/4/6/8/9/12/16) that fits `n` cameras, capped at
// the largest layout. Returns a NUMBER (playback uses a numeric conf), so it sizes the view-based
// playback grid to a loaded view's camera span. Replaces mcplayback's old deriveConf (which capped at 4).
export const tilesForCount = (n) => {
  const options = LAYOUTS.map((l) => l.tiles); // [1, 2, 4, 6, 8, 9, 12, 16]
  const want = Math.max(1, Math.floor(Number(n) || 0));
  return options.find((t) => t >= want) || options[options.length - 1];
};

// ── ARRANGE densities ───────────────────────────────────────────────────────
// Tiles-per-page options the Arrange bar offers. Each is a SYMMETRIC grid
// (uniform cells for placing cameras), unlike the asymmetric live conf-8. The
// full grid always renders in Arrange (empty cells = drop targets), so rows is
// fixed here, not computed. Bump the ceiling by uncommenting a line.
export const ARRANGE_DENSITIES = [
  { tiles: 4,  cols: 2, rows: 2 },
  { tiles: 8,  cols: 4, rows: 2 },
  { tiles: 16, cols: 4, rows: 4 },
  // 32 (8×4) + 64 (8×8) for large cross-NVR views (#86bb7phhh). 64 = the 64-slot
  // ceiling rendered as ONE 8×8 grid, so it doubles as "All" — every slot on screen at
  // once, no paging, no scroll, drag across the whole set. No separate 'all' sentinel is
  // needed because nothing exceeds 64. Gated to landscape+desktop in the UI (LiveView
  // density control, `.live-arrange-density__opt--wide`); tablet-portrait keeps 4/8/16.
  { tiles: 32, cols: 8, rows: 4 },
  { tiles: 64, cols: 8, rows: 8 },
  // { tiles: 36, cols: 6, rows: 6 },
];

// Density steps offered only at landscape+desktop (>=1024). Tablet-portrait + smaller
// stay on the base 4/8/16 set. Kept as data here so the gating is one source of truth.
export const ARRANGE_WIDE_TILE_OPTIONS = [32, 64];

// The media query the wide steps are gated behind. MUST stay in sync with the
// `.live-arrange-seg__btn--wide` @media in liveStyle.css (landscape tablet + desktop). LiveView reads
// this via matchMedia to clamp a wide density back to 16 when the viewport leaves the tier (e.g. a
// tablet rotates landscape→portrait) — the control is hidden there, so the state must follow.
export const ARRANGE_WIDE_TIER_MQ = '(min-width: 1024px) and (orientation: landscape), (min-width: 1280px)';

// Tablet tier (1024–1279, both orientations): the Live bottom-bar selectors (Overlays / Layout / System
// info) render their content as a bottom SHEET here — the same mobile presentation "bubbling up" — and only
// flip to a desktop popover at >=1280 (the touch->mouse line). Mobile (<1024) already uses sheets via its own
// LiveView path; this covers the range where DesktopBottomBar paints but input is still touch-first (#86bb7phhh).
export const TABLET_SHEET_MQ = '(min-width: 1024px) and (max-width: 1279px)';

export const ARRANGE_TILE_OPTIONS = ARRANGE_DENSITIES.map((d) => d.tiles);
export const ARRANGE_MAX_DENSITY = ARRANGE_TILE_OPTIONS[ARRANGE_TILE_OPTIONS.length - 1];

// The working camera array selectView materializes is 64 slots, so that is the
// hard ceiling on (pages × density) until the array itself grows.
export const ARRANGE_MAX_SLOTS = 64;

// Default canvas density on ENTRY (David 2026-07-31: 16 reads cleaner on tablet —
// a 30-cam view is 2 clean page-tabs at 16 vs 4 scrolling tabs at 8). Tablet portrait
// + landscape + desktop all default to 16; small views still auto-fit DOWN.
export const DEFAULT_DENSITY_TABLET = 16;
export const DEFAULT_DENSITY_DESKTOP = 16;

export const arrangeGeometry = (tiles) =>
  ARRANGE_DENSITIES.find((d) => d.tiles === tiles) || ARRANGE_DENSITIES[0];
